import { ReactLiquid, useLiquid } from "react-liquid";
import { Liquid } from 'liquidjs';
import React from 'react';

import { remapCampaignVariables } from "../utils";
import { parseValue } from "./scripts";
import { useContext } from "react";
import { PageContext } from "./PageContext";
import { ContainerContext } from "../WebComponents/v1/container/ContainerContext";
import StripePaymentElement from "../WebComponents/v2/PaymentElement/live";
import { keys } from "lodash";

// Error boundary component for Liquid rendering
class LiquidErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.warn('Liquid rendering error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Fallback to displaying the original HTML content
      return <div dangerouslySetInnerHTML={{ __html: this.props.fallbackHtml || '' }} />;
    }

    return this.props.children;
  }
}

export const defaultContext = { 
  typeface: {
    display: "Roboto",
    headline: "Roboto",
    title: "Roboto",
    body: "Roboto",
    label: "Roboto",    
  },
  typescale: {
    display: {
      small: "36pt",
      medium: "45pt",
      large: "57pt",
    },
    headline: {
      small: "24pt",
      medium: "28pt",
      large: "32pt",
    },
    title: {
      small: "14pt",
      medium: "16pt",
      large: "22pt",
    },
    body: {
      small: "12pt",
      medium: "14pt",
      large: "16pt",
    },
    label: {
      small: "11pt",
      medium: "12pt",
      large: "20pt",
    },
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
    xxl: "2.5rem",
  }, 
  layout: {
    xs: "25rem",
    sm: "37.5rem",
    md: "50rem",
    lg: "62.5rem",
    xl: "75rem",
    xxl: "100%",
  },
};

const withDefaults = (data: any) => {

  // Deep merge defaultContext with data, merging subproperties for matching keys
  let result = defaultContext;

  for (const key in data) {
    if (
      data.hasOwnProperty(key) &&
      defaultContext.hasOwnProperty(key) &&
      typeof defaultContext[key] === "object" &&
      defaultContext[key] !== null &&
      !Array.isArray(defaultContext[key]) &&
      typeof data[key] === "object" &&
      data[key] !== null &&
      !Array.isArray(data[key])
    ) {
      // Merge subproperties, data takes priority
      result[key] = { ...defaultContext[key], ...data[key] };
    } else {
      // Use data value
      result[key] = data[key];
    }
  }

  // support for legacy key's (colors -> color)
  if (data?.color) result.colors = data?.color;
  if (data?.colors) result.color = data?.colors;

  return result;
};

export const shouldParseLiquid = (html: string): boolean => {
  const pageContext = useContext(PageContext);
  
  if (!html) return false;
  return Boolean(pageContext?.parseLiquid) && html.includes("{{") && html.includes("}}");
};

export const LiquidStyles = () => {
  const pageContext = useContext(PageContext);
  const data = {
    ...withDefaults({
      ...remapCampaignVariables(pageContext?.campaignSettings?.contentVariables),
      ...remapCampaignVariables(pageContext?.campaignSettings?.variables),
    }),
  };

  return (
    <style>
      {`
        .wc-Section .wc-Headline {
          font-family: '${data?.typeface?.headline}', sans-serif;
          color: ${data?.color?.onSurface || "#141414"};
        }
        .wc-Section .wc-Text, .wc-Section .wc-Button, .wc-Section .wc-AdvancedButton, .wc-Section .wc-ListItem, .wc-Section .wc-List {
          font-family: '${data?.typeface?.body}', sans-serif;
          color: ${data?.color?.onSurface || "#141414"};
        }
      `}
    </style>
  );
};

export const LiquidRenderer = ({ html, data = {} }) => {
  const pageContext = useContext(PageContext);
  const containerContext = useContext(ContainerContext);
  
  // console.log("LiquidRendererComponent", html, shouldParseLiquid(html))
  // if (!shouldParseLiquid(html)) return html; // why is this rendering opposite
  
  if (html?.includes("{{ stripe_payment_element }}")) return <StripePaymentElement />;
  
  // Add error handling for malformed Liquid templates
  const safeTemplate = (() => {
    try {
      const parsedValue = parseValue(html);
      // Basic validation to check for common malformed Liquid syntax
      if (typeof parsedValue === 'string') {
        // Check for unclosed Liquid tags
        const openTags = (parsedValue.match(/{{/g) || []).length;
        const closeTags = (parsedValue.match(/}}/g) || []).length;
        
        if (openTags !== closeTags) {
          console.warn('Malformed Liquid template detected - unclosed tags:', parsedValue);
          return html || ''; // Return original HTML if template is malformed
        }
        
        // Check for other common malformed patterns
        if (parsedValue.includes('{{') && !parsedValue.includes('}}')) {
          console.warn('Malformed Liquid template detected - missing closing braces:', parsedValue);
          return html || '';
        }
        
        return parsedValue;
      }
      return parsedValue;
    } catch (error) {
      console.warn('Error parsing Liquid template:', error, 'Original HTML:', html);
      return html || ''; // Return original HTML if parsing fails
    }
  })();
  
  return (
    <LiquidErrorBoundary fallbackHtml={html}>
      <ReactLiquid
        // template={html}
        template={safeTemplate}
        data={withDefaults({
          ...remapCampaignVariables(pageContext?.campaignSettings?.contentVariables),
          ...remapCampaignVariables(pageContext?.campaignSettings?.variables),
          ...data,
          ...containerContext,
        })}
        html
      />
    </LiquidErrorBoundary>
  );
};

export const getRepeatContent = (var_name) => {
  if (!var_name) return;
  const pageContext = useContext(PageContext);
  const data = {
    ...remapCampaignVariables(pageContext?.campaignSettings?.contentVariables),
    ...remapCampaignVariables(pageContext?.campaignSettings?.variables),
  };
  const toLoopAround = data?.[var_name];
  // console.log({ toLoopAround, var_name, data });
  return toLoopAround;
};

// This function recursively parses an object to process any string values using the parseLiquidString function.
// It handles both arrays and objects, ensuring that all string values within the structure are parsed.
export const parseLiquidObject = (obj: any, forceContext = {}): any => {
  // console.log("parseLiquidObject", obj);
  if (obj === null || typeof obj !== 'object') return obj;
  const pageContext = keys(forceContext).length > 0 ? forceContext : useContext(PageContext);

  if (Array.isArray(obj)) {
    return obj.map(item => parseLiquidObject(item, pageContext));
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      typeof value === "string"
        ? parseLiquidStringWithLiquidJS(value, pageContext)
        : parseLiquidObjectWithLiquidJS(value, pageContext),
    ])
  );
};

export const parseLiquidString = (str: string, forceContext = {}) => {
  if (!str) return str;

  if (!shouldParseLiquid(str)) return str;

  let pageContext = {};
  if (keys(forceContext).length > 0) {
    pageContext = forceContext;
  } else {
    pageContext = useContext(PageContext);
  }
  
  const data = {
    ...remapCampaignVariables(pageContext?.campaignSettings?.contentVariables),
    ...remapCampaignVariables(pageContext?.campaignSettings?.variables),
  };
  try {
    const { markup } = useLiquid(str, data);
    return markup || str;
  } catch (e) {
    console.log("Error parsing liquid", e);
    return str;
  }
};

export const parseLiquidStringWithLiquidJS = (str: string, forceContext = {}): string => {
  if (!str) return str;

  // Check if we should parse liquid at all
  if (!str.includes("{{") || !str.includes("}}")) {
    return str;
  }

  // Additional validation for malformed Liquid templates
  const openTags = (str.match(/{{/g) || []).length;
  const closeTags = (str.match(/}}/g) || []).length;
  
  if (openTags !== closeTags) {
    console.warn('Malformed Liquid template detected - unclosed tags:', str);
    return str; // Return original string if malformed
  }

  const data = withDefaults({
    ...remapCampaignVariables(forceContext?.campaignSettings?.contentVariables),
    ...remapCampaignVariables(forceContext?.campaignSettings?.variables),
  });

  try {
    const liquid = new Liquid();
    const result = liquid.parseAndRenderSync(str, data);
    return result;
  } catch (e) {
    console.warn("Error parsing liquid with LiquidJS", e, "Template:", str);
    // Return the original string if parsing fails
    return str;
  }
};

export const parseLiquidObjectWithLiquidJS = (obj: any, forceContext = {}): any => {
  if (obj === null || typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => parseLiquidObjectWithLiquidJS(item, forceContext));
  }

  try {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        typeof value === "string"
          ? parseLiquidStringWithLiquidJS(value, forceContext)
          : parseLiquidObjectWithLiquidJS(value, forceContext),
      ])
    );
  } catch (e) {
    console.log("Error parsing liquid object with LiquidJS", e);
    return obj;
  }
};
